import { type JSX, useEffect, useState } from "react";

import { type Operation, type ResolvedExample, resolveExample } from "../api/catalog";
import { BenchApiError } from "../api/http";

interface ExamplePickerProps {
  operation: Operation;
  onLoad: (example: ResolvedExample) => void;
}

/**
 * Pick a worked example and load it into the form.
 *
 * Examples are resolved server-side at the moment of selection, not when the catalogue is built:
 * roughly half the surface takes an id, and those only exist once something has written them. So
 * choosing "the most recent memory" fills in a ULID that actually exists.
 */
export function ExamplePicker({ operation, onLoad }: ExamplePickerProps): JSX.Element | null {
  const [selected, setSelected] = useState("");
  const [resolved, setResolved] = useState<ResolvedExample | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A different operation means a different example list.
  useEffect(() => {
    setSelected("");
    setResolved(null);
    setError(null);
  }, [operation.operation_id]);

  if (operation.examples.length === 0) {
    return null;
  }

  const choose = (name: string): void => {
    setSelected(name);
    setResolved(null);
    setError(null);
    if (!name) {
      return;
    }

    setLoading(true);
    void resolveExample(operation.operation_id, name)
      .then((example) => {
        setResolved(example);
        onLoad(example);
      })
      .catch((cause: unknown) => {
        setError(
          cause instanceof BenchApiError
            ? cause.message
            : `Could not load that example: ${String(cause)}`,
        );
      })
      .finally(() => setLoading(false));
  };

  const chosen = operation.examples.find((example) => example.name === selected);

  return (
    <section className="examples">
      <label className="examples__field">
        <span>Worked example</span>
        <select value={selected} onChange={(event) => choose(event.target.value)}>
          <option value="">Start from an empty form…</option>
          {operation.examples.map((example) => (
            <option key={example.name} value={example.name}>
              {example.summary}
            </option>
          ))}
        </select>
      </label>

      {loading && <p className="muted">Filling the form from live data…</p>}

      {error && (
        <div className="notice notice--error">
          <strong>That example could not be loaded.</strong> {error}
        </div>
      )}

      {chosen?.note && !loading && <p className="muted examples__note">{chosen.note}</p>}

      {resolved && resolved.unresolved.length > 0 && (
        <div className="notice notice--warning">
          <strong>
            {resolved.unresolved.length === 1
              ? "One value could not be filled in."
              : `${resolved.unresolved.length} values could not be filled in.`}
          </strong>
          <ul>
            {resolved.hints.map((hint, index) => (
              <li key={resolved.unresolved[index] ?? index}>
                <code>{resolved.unresolved[index]}</code> — {hint}
              </li>
            ))}
          </ul>
        </div>
      )}

      {resolved && resolved.unresolved.length === 0 && (
        <p className="muted">Every field is filled in — this is ready to run.</p>
      )}
    </section>
  );
}
